import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Plus, X, Loader2, RotateCcw, Save } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

/**
 * Admin-only card that lets superusers tune the banned-phrases list used by
 * the language-polish layer of the post-generation pipeline. Defaults are
 * read-only (they ship in code). Extra phrases are stored in MongoDB and
 * merged in at runtime, so admins can add company-specific jargon to ban
 * without a deploy.
 */
export const WritingStyleCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const [extras, setExtras] = useState([]);
  const [pristine, setPristine] = useState([]);
  const [input, setInput] = useState('');
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const reload = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getWritingStyle();
      setDefaults(data?.default_banned_phrases || []);
      setExtras(data?.extra_banned_phrases || []);
      setPristine(data?.extra_banned_phrases || []);
      setMeta({ updated_at: data?.updated_at || null, updated_by: data?.updated_by || null });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load writing style settings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const dirty = useMemo(() => {
    if (pristine.length !== extras.length) return true;
    const a = [...pristine].sort();
    const b = [...extras].sort();
    return a.some((v, i) => v !== b[i]);
  }, [extras, pristine]);

  const addPhrase = () => {
    const v = input.trim();
    if (!v) return;
    if (v.length > 80) {
      toast.error('Phrase is too long (max 80 chars).');
      return;
    }
    const key = v.toLowerCase();
    const alreadyDefault = defaults.some((d) => d.toLowerCase() === key);
    if (alreadyDefault) {
      toast.info(`"${v}" is already in the default banned list.`);
      setInput('');
      return;
    }
    const alreadyExtra = extras.some((d) => d.toLowerCase() === key);
    if (alreadyExtra) {
      toast.info(`"${v}" is already in your custom list.`);
      setInput('');
      return;
    }
    if (extras.length >= 100) {
      toast.error('Maximum 100 custom phrases.');
      return;
    }
    setExtras([...extras, v]);
    setInput('');
  };

  const removePhrase = (p) => setExtras(extras.filter((x) => x !== p));

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addPhrase();
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.updateWritingStyle(extras);
      setExtras(res?.extra_banned_phrases || []);
      setPristine(res?.extra_banned_phrases || []);
      setMeta({ updated_at: res?.updated_at || null, updated_by: meta.updated_by });
      toast.success('Writing style updated. New posts will use this immediately.');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => setExtras(pristine);

  return (
    <Card className="border-border" data-testid="admin-writing-style-card">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Sparkles size={18} className="text-[hsl(var(--accent))]" />
          <h2 className="font-semibold">Writing style controls</h2>
          <Badge
            variant="secondary"
            className="font-mono-edge text-[11px] ml-1"
            data-testid="admin-writing-style-count"
          >
            {extras.length} custom · {defaults.length} default
          </Badge>
          {meta.updated_at && (
            <span className="text-[11px] text-muted-foreground ml-auto hidden sm:block">
              Updated by {meta.updated_by || 'admin'}
            </span>
          )}
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Words or phrases the language polish layer is told to{' '}
            <span className="font-medium text-foreground">avoid</span> when rewriting posts. The
            defaults ship with the product; add company-specific jargon you do not want appearing
            in generated content (e.g. internal acronyms, banned superlatives, marketing clichés).
          </p>

          {/* Add input */}
          <div className="flex gap-2 items-center">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={loading ? 'Loading…' : 'Add a phrase (press Enter or comma)'}
              disabled={loading || saving}
              maxLength={80}
              className="flex-1"
              data-testid="admin-writing-style-input"
            />
            <Button
              type="button"
              onClick={addPhrase}
              disabled={!input.trim() || saving}
              className="rounded-lg"
              data-testid="admin-writing-style-add"
            >
              <Plus size={14} className="mr-1.5" />
              Add
            </Button>
          </div>

          {/* Custom phrases */}
          <div>
            <p className="text-[11px] font-mono-edge text-muted-foreground tracking-wide mb-2">
              CUSTOM ({extras.length})
            </p>
            {extras.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No custom phrases yet. Defaults below are always enforced.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="admin-writing-style-extras">
                {extras.map((p) => (
                  <Badge
                    key={p}
                    variant="secondary"
                    className="font-mono-edge text-[11px] flex items-center gap-1 pl-2 pr-1 py-0.5"
                    data-testid={`admin-writing-style-extra-${p}`}
                  >
                    {p}
                    <button
                      type="button"
                      onClick={() => removePhrase(p)}
                      disabled={saving}
                      aria-label={`Remove ${p}`}
                      className="rounded-sm hover:bg-foreground/10 p-0.5"
                      data-testid={`admin-writing-style-remove-${p}`}
                    >
                      <X size={10} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Defaults (read-only) */}
          <div>
            <p className="text-[11px] font-mono-edge text-muted-foreground tracking-wide mb-2">
              DEFAULTS · read-only ({defaults.length})
            </p>
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" data-testid="admin-writing-style-defaults">
              {defaults.map((d) => (
                <Badge
                  key={d}
                  variant="outline"
                  className="font-mono-edge text-[10.5px] text-muted-foreground border-border/70 px-1.5 py-0"
                >
                  {d}
                </Badge>
              ))}
            </div>
          </div>

          {/* Save / reset */}
          {dirty && (
            <div
              className="flex items-center gap-2 pt-2 border-t border-border"
              data-testid="admin-writing-style-actions"
            >
              <Button
                onClick={onSave}
                disabled={saving}
                className="rounded-lg"
                data-testid="admin-writing-style-save"
              >
                {saving ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Save size={14} className="mr-1.5" />
                )}
                Save changes
              </Button>
              <Button
                variant="ghost"
                onClick={onReset}
                disabled={saving}
                className="rounded-lg"
                data-testid="admin-writing-style-reset"
              >
                <RotateCcw size={14} className="mr-1.5" />
                Discard
              </Button>
              <span className="text-[11px] text-muted-foreground">
                {extras.length - pristine.length > 0
                  ? `+${extras.length - pristine.length} pending`
                  : extras.length < pristine.length
                  ? `${pristine.length - extras.length} pending removal`
                  : 'changes pending'}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WritingStyleCard;
